import React from "react";
import closeIcon from "@src/assets/svg/close-icon-gray.svg";
import eyeIcon from "@src/assets/svg/eye-icon.svg";
import eyeSlashIcon from "@src/assets/svg/eye-slash-icon.svg";
import styles from "./ota-create-backup-modal.module.scss";
import { FlexColumnContainer } from "@src/components/templates/layouts/flex-column-container/flex-column-container";
import { PrivateKeyUploadField } from "@src/components/atoms/private-key-upload-field/private-key-upload-field";
import { OTASecondaryButton } from "@src/components/atoms/buttons/ota-secondary-button/ota-secondary-button";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";
import { useOtaCreateBackupModal } from "./use-ota-create-backup-modal";

const cssClassName = "ota-create-backup-modal";

interface OtaCreateBackupModalProps {
  onClose: () => void;
}

const OtaCreateBackupModal = ({ onClose }: OtaCreateBackupModalProps) => {
  const {
    texts,
    targetPath,
    sourceMachineName,
    sourceMachineIp,
    username,
    password,
    privateKeyFile,
    isPasswordVisible,
    isAskForApprovalLoading,
    handleCloseModal,
    handleTargetPathChange,
    handleUsernameChange,
    handlePasswordChange,
    handlePrivateKeyFileSelection,
    handleTogglePasswordVisibility,
    handleAskForApproval,
  } = useOtaCreateBackupModal(onClose);

  return (
    <div className={styles[cssClassName]}>
      <div
        className={styles[`${cssClassName}__overlay`]}
        aria-label={texts.closeCreateBackupModalAriaLabel}
      />

      <div className={styles[`${cssClassName}__dialog-wrapper`]}>
        <div
          className={styles[`${cssClassName}__modal-body`]}
          role="dialog"
          aria-modal="true"
        >
          <div className={styles[`${cssClassName}__header`]}>
            <h2 className={styles[`${cssClassName}__title`]}>{texts.title}</h2>
            <IconButton
              fun={handleCloseModal}
              icon={closeIcon}
              tooltipPosition="top"
              variant="close-button"
              disabled={isAskForApprovalLoading}
            />
          </div>

          <div className={styles[`${cssClassName}__form`]}>
            <FlexColumnContainer verticalGap={10} alignItems="stretch">
              <FlexColumnContainer verticalGap={5} alignItems="stretch">
                <label
                  className={styles[`${cssClassName}__field-label`]}
                  htmlFor="create-backup-target-path"
                >
                  {texts.targetPathLabel}
                </label>
                <input
                  id="create-backup-target-path"
                  type="text"
                  value={targetPath}
                  onChange={handleTargetPathChange}
                  placeholder={texts.targetPathPlaceholder}
                  className={styles[`${cssClassName}__text-input`]}
                />
              </FlexColumnContainer>

              <FlexColumnContainer verticalGap={5} alignItems="stretch">
                <p className={styles[`${cssClassName}__section-title`]}>
                  {texts.sourceMachineLabel}
                </p>

                <div className={styles[`${cssClassName}__machine-card`]}>
                  <p className={styles[`${cssClassName}__machine-name`]}>
                    {sourceMachineName}{" "}
                    {sourceMachineIp && (
                      <span className={styles[`${cssClassName}__machine-ip`]}>
                        ({sourceMachineIp})
                      </span>
                    )}
                  </p>
                </div>
              </FlexColumnContainer>

              <FlexColumnContainer verticalGap={5} alignItems="stretch">
                <label
                  className={styles[`${cssClassName}__field-label`]}
                  htmlFor="create-backup-username"
                >
                  {texts.usernameLabel}
                </label>
                <input
                  id="create-backup-username"
                  type="text"
                  value={username}
                  onChange={handleUsernameChange}
                  placeholder={texts.usernamePlaceholder}
                  className={styles[`${cssClassName}__text-input`]}
                />
              </FlexColumnContainer>

              <FlexColumnContainer verticalGap={5} alignItems="stretch">
                <label
                  className={styles[`${cssClassName}__field-label`]}
                  htmlFor="create-backup-password"
                >
                  {texts.passwordLabel}
                </label>
                <div
                  className={styles[`${cssClassName}__password-input-wrapper`]}
                >
                  <input
                    id="create-backup-password"
                    type={isPasswordVisible ? "text" : "password"}
                    value={password}
                    onChange={handlePasswordChange}
                    placeholder={texts.passwordPlaceholder}
                    className={styles[`${cssClassName}__password-input`]}
                  />
                  <IconButton
                    fun={handleTogglePasswordVisibility}
                    icon={isPasswordVisible ? eyeSlashIcon : eyeIcon}
                    tooltip={
                      isPasswordVisible
                        ? texts.hidePasswordTooltip
                        : texts.showPasswordTooltip
                    }
                    tooltipPosition="top"
                    variant="no-padding"
                  />
                </div>
              </FlexColumnContainer>

              <PrivateKeyUploadField
                label={texts.privateKeyFileLabel}
                fileName={privateKeyFile?.name ?? ""}
                onFileSelect={handlePrivateKeyFileSelection}
              />

              <p className={styles[`${cssClassName}__helper-text`]}>
                {texts.helperText}
              </p>
            </FlexColumnContainer>
          </div>

          <div className={styles[`${cssClassName}__buttons-wrapper`]}>
            <OTASecondaryButton
              fun={handleCloseModal}
              text={texts.cancelButtonText}
              disabled={isAskForApprovalLoading}
            />

            <ButtonWithLoader
              fun={handleAskForApproval}
              text={texts.askForApprovalButtonText}
              loading={isAskForApprovalLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OtaCreateBackupModal;
